/**
 * KategorieBadge — the Rechnungskontrolle deviation category K0–K3 as a soft
 * badge (design.md §5 — tinted fill + own-family border + strong text). Ported
 * from the reference prototype's kategorie-badge.tsx onto our `Badge` primitive.
 *
 * Escalation ladder (design.md §2 — K0–K3 is THE example: the resting pole is
 * Neutral, NOT green; only the concerning pole colours in, so the scale reads as
 * a gradient Neutral → Info → Warning → Danger, not a rainbow. Green is reserved
 * for genuine success events). The scope contract (design.md WINS over the v0
 * prototype's K0→ok) resolves this in design.md's favour:
 *   K0 keine Differenz     → neutral   (grau — resting state, no deviation)
 *   K1 geringfügig         → info      (blau — auto-release with note)
 *   K2 mittel              → warning   (orange — manual review)
 *   K3 grösser / Pflicht   → danger    (rot — blocked)
 *
 * When the K3 rating is FORCED by a violated Pflichtregel (not the amount), the
 * badge shows a small lock ("Pflichtregel verletzt") so it's clear the rating
 * isn't from the deviation. The wire may not carry that flag yet → hidden when
 * absent (probed defensively upstream).
 *
 * [Source: brokly-design-reference/components/brokly/kategorie-badge.tsx]
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §2/§5]
 */
import { Lock } from 'lucide-react';
import { Badge, type BadgeVariant } from '../../../components/ui/Badge';
import type { DiffCategory } from '@brokly/shared';

const KATEGORIE_VARIANT: Record<DiffCategory, BadgeVariant> = {
  K0: 'neutral',
  K1: 'info',
  K2: 'warning',
  K3: 'danger',
};

export function KategorieBadge({
  kategorie,
  pflichtregel = false,
  className,
}: {
  kategorie: DiffCategory;
  /** Only relevant on K3: the rating stems from a violated Pflichtregel. */
  pflichtregel?: boolean;
  className?: string;
}) {
  return (
    <Badge variant={KATEGORIE_VARIANT[kategorie]} className={className}>
      {kategorie}
      {pflichtregel && <Lock aria-label="Pflichtregel verletzt" />}
    </Badge>
  );
}
